import { useState } from 'react';
import { motion } from 'framer-motion';
import { FileText, Download, ArrowLeft } from 'lucide-react';
import jsPDF from 'jspdf';
import toast, { Toaster } from 'react-hot-toast';

export default function ContractDemo() {
  const [formData, setFormData] = useState({
    contractType: 'jual-beli',
    party1Name: '',
    party1Address: '',
    party2Name: '',
    party2Address: '',
    objectDescription: '',
    price: '',
    date: new Date().toISOString().split('T')[0],
  });

  const generatePDF = () => {
    console.log('PDF Generation Started');
    try {
      // Validation
      if (!formData.party1Name || !formData.party2Name || !formData.objectDescription) {
        console.log('Validation failed - missing required fields');
        toast.error('Mohon lengkapi data yang diperlukan!');
        return;
      }

      console.log('Validation passed, showing loading toast');
      // Show loading toast
      const loadingToast = toast.loading('Membuat kontrak PDF...');
      
      setTimeout(() => {
        try {
          console.log('Initializing jsPDF...');
          // Initialize jsPDF with mobile-friendly settings
          const doc = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4',
            compress: true,
            putOnlyUsedFonts: true,
            floatPrecision: 16
          });
          console.log('jsPDF initialized successfully');

          const pageWidth = doc.internal.pageSize.getWidth();
          const pageHeight = doc.internal.pageSize.getHeight();
          let y = 20;
          const margin = 20;
          const contentWidth = pageWidth - (margin * 2);
          const lineHeight = 6;
          
          // Helper function to add new page if needed
          const checkPageBreak = (requiredHeight: number) => {
            if (y + requiredHeight > pageHeight - margin) {
              doc.addPage();
              y = margin;
            }
          };

          // Title - Center aligned
          doc.setFontSize(16);
          doc.setFont('helvetica', 'bold');
          const titleText = 'SURAT PERJANJIAN ' + formData.contractType.toUpperCase().replace('-', ' ');
          const titleWidth = doc.getTextWidth(titleText);
          doc.text(titleText, (pageWidth - titleWidth) / 2, y);
          y += lineHeight * 2;
          
          // Date
          doc.setFontSize(11);
          doc.setFont('helvetica', 'normal');
          const dateText = 'Pada hari ini, ' + new Date(formData.date).toLocaleDateString('id-ID', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          });
          doc.text(dateText, margin, y);
          y += lineHeight * 2;
          
          // Introduction
          doc.text('Yang bertanda tangan di bawah ini:', margin, y);
          y += lineHeight * 2;
          
          // Pihak 1
          checkPageBreak(lineHeight * 4);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(11);
          doc.text('PIHAK PERTAMA:', margin, y);
          y += lineHeight;
          doc.setFont('helvetica', 'normal');
          doc.text('Nama    : ' + formData.party1Name, margin, y);
          y += lineHeight;
          doc.text('Alamat  : ' + formData.party1Address, margin, y);
          y += lineHeight * 2;
          
          // Pihak 2
          checkPageBreak(lineHeight * 4);
          doc.setFont('helvetica', 'bold');
          doc.text('PIHAK KEDUA:', margin, y);
          y += lineHeight;
          doc.setFont('helvetica', 'normal');
          doc.text('Nama    : ' + formData.party2Name, margin, y);
          y += lineHeight;
          doc.text('Alamat  : ' + formData.party2Address, margin, y);
          y += lineHeight * 2;
          
          // Pasal 1 - Object
          checkPageBreak(lineHeight * 3);
          doc.setFont('helvetica', 'bold');
          doc.text('Pasal 1 - Objek Perjanjian', margin, y);
          y += lineHeight;
          doc.setFont('helvetica', 'normal');
          
          const objText = doc.splitTextToSize(formData.objectDescription, contentWidth);
          checkPageBreak(lineHeight * (objText.length + 1));
          doc.text(objText, margin, y);
          y += lineHeight * (objText.length + 1);
          
          // Pasal 2 - Price (if provided)
          if (formData.price && parseInt(formData.price) > 0) {
            checkPageBreak(lineHeight * 3);
            doc.setFont('helvetica', 'bold');
            doc.text('Pasal 2 - Harga', margin, y);
            y += lineHeight;
            doc.setFont('helvetica', 'normal');
            const priceText = 'Harga yang disepakati adalah Rp ' + parseInt(formData.price).toLocaleString('id-ID');
            doc.text(priceText, margin, y);
            y += lineHeight * 2;
          }
          
          // Signature section
          checkPageBreak(lineHeight * 8);
          y += lineHeight;
          doc.text('Pertama,', 40, y);
          doc.text('Kedua,', 140, y);
          y += lineHeight * 5;
          doc.text('(' + formData.party1Name + ')', 40, y);
          doc.text('(' + formData.party2Name + ')', 140, y);
          
          // Generate filename
          const fileName = `kontrak_${formData.contractType}_${new Date().toISOString().split('T')[0]}.pdf`;
          console.log('Generated filename:', fileName);
          
          // Enhanced PDF download with fallback methods
          console.log('Starting PDF download...');
          try {
            // Method 1: Standard jsPDF save
            doc.save(fileName);
            console.log('PDF saved via standard method');
          } catch (saveError) {
            console.log('Standard save failed, trying Blob method...', saveError);
            
            // Method 2: Manual Blob download as fallback
            try {
              const pdfOutput = doc.output('blob');
              const url = URL.createObjectURL(pdfOutput);
              const link = document.createElement('a');
              link.href = url;
              link.download = fileName;
              link.style.display = 'none';
              
              // Trigger download
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              
              // Clean up
              setTimeout(() => URL.revokeObjectURL(url), 100);
              console.log('PDF downloaded via Blob method');
            } catch (blobError) {
              console.error('Blob method also failed:', blobError);
              // Method 3: Data URL fallback
              try {
                const dataUrl = doc.output('dataurlstring');
                const link = document.createElement('a');
                link.href = dataUrl;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                console.log('PDF downloaded via data URL method');
              } catch (dataUrlError) {
                console.error('All download methods failed:', dataUrlError);
                throw new Error('Failed to download PDF with all methods');
              }
            }
          }
          
          toast.dismiss(loadingToast);
          toast.success('Kontrak PDF berhasil didownload!', {
            duration: 4000,
          });
          
        } catch (pdfError) {
          console.error('PDF Generation Error:', pdfError);
          console.error('Error stack:', pdfError.stack);
          toast.dismiss(loadingToast);
          toast.error('Gagal membuat PDF. Silakan coba lagi atau hubungi administrator.', {
            duration: 5000,
          });
        }
      }, 500); // Small delay for better UX
        
    } catch (error) {
      console.error('PDF Error:', error);
      toast.error('Terjadi kesalahan saat membuat PDF. Silakan refresh halaman dan coba lagi.');
    }
  };

  return (
    <div className="min-h-screen bg-dark p-4">
      <Toaster position="top-right" />
      
      <div className="container mx-auto max-w-4xl">
        {/* Header */}
        <div className="glass-card p-4 mb-6 flex items-center gap-4">
          <a href="/#projects" className="text-neon-blue hover:text-neon-green transition">
            <ArrowLeft size={24} />
          </a>
          <div>
            <h1 className="text-2xl font-bold text-white">Contract Generator</h1>
            <p className="text-sm text-gray-400">Demo Generator Kontrak Hukum</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Form */}
          <div className="glass-card p-6 space-y-4">
            <h2 className="text-xl font-bold text-white mb-4">Data Kontrak</h2>
            
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                Jenis Kontrak
              </label>
              <select
                value={formData.contractType}
                onChange={(e) => setFormData({ ...formData, contractType: e.target.value })}
                className="w-full px-4 py-3 bg-dark-lighter border border-neon-blue/30 rounded-xl text-white focus:outline-none focus:border-neon-blue"
              >
                <option value="jual-beli">Jual Beli</option>
                <option value="sewa-menyewa">Sewa Menyewa</option>
                <option value="kerjasama">Kerjasama</option>
                <option value="pekerjaan">Pekerjaan</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                Tanggal Kontrak
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="w-full px-4 py-3 bg-dark-lighter border border-neon-blue/30 rounded-xl text-white focus:outline-none focus:border-neon-blue"
              />
            </div>

            <div className="border-t border-gray-700 pt-4">
              <h3 className="font-bold text-neon-blue mb-3">Pihak Pertama</h3>
              <div className="space-y-3">
                <input
                  type="text"
                  placeholder="Nama Lengkap"
                  value={formData.party1Name}
                  onChange={(e) => setFormData({ ...formData, party1Name: e.target.value })}
                  className="w-full px-4 py-3 bg-dark-lighter border border-neon-blue/30 rounded-xl text-white focus:outline-none focus:border-neon-blue"
                />
                <input
                  type="text"
                  placeholder="Alamat"
                  value={formData.party1Address}
                  onChange={(e) => setFormData({ ...formData, party1Address: e.target.value })}
                  className="w-full px-4 py-3 bg-dark-lighter border border-neon-blue/30 rounded-xl text-white focus:outline-none focus:border-neon-blue"
                />
              </div>
            </div>

            <div className="border-t border-gray-700 pt-4">
              <h3 className="font-bold text-neon-green mb-3">Pihak Kedua</h3>
              <div className="space-y-3">
                <input
                  type="text"
                  placeholder="Nama Lengkap"
                  value={formData.party2Name}
                  onChange={(e) => setFormData({ ...formData, party2Name: e.target.value })}
                  className="w-full px-4 py-3 bg-dark-lighter border border-neon-green/30 rounded-xl text-white focus:outline-none focus:border-neon-green"
                />
                <input
                  type="text"
                  placeholder="Alamat"
                  value={formData.party2Address}
                  onChange={(e) => setFormData({ ...formData, party2Address: e.target.value })}
                  className="w-full px-4 py-3 bg-dark-lighter border border-neon-green/30 rounded-xl text-white focus:outline-none focus:border-neon-green"
                />
              </div>
            </div>

            <div className="border-t border-gray-700 pt-4">
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                Deskripsi Objek Perjanjian
              </label>
              <textarea
                rows={4}
                placeholder="Jelaskan objek atau isi perjanjian..."
                value={formData.objectDescription}
                onChange={(e) => setFormData({ ...formData, objectDescription: e.target.value })}
                className="w-full px-4 py-3 bg-dark-lighter border border-neon-blue/30 rounded-xl text-white focus:outline-none focus:border-neon-blue resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                Harga / Nilai (Opsional)
              </label>
              <input
                type="number"
                placeholder="0"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                className="w-full px-4 py-3 bg-dark-lighter border border-neon-blue/30 rounded-xl text-white focus:outline-none focus:border-neon-blue"
              />
            </div>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={generatePDF}
              disabled={!formData.party1Name || !formData.party2Name || !formData.objectDescription}
              className="w-full btn-neon-green flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed py-3 text-sm md:text-base"
            >
              <Download size={20} />
              Generate & Download PDF
              <span className="hidden sm:inline">📄</span>
            </motion.button>
            
            {/* Mobile-specific helper text */}
            <div className="text-xs text-gray-500 text-center md:hidden">
              💡 Tip: Pastikan koneksi internet stabil untuk download PDF
            </div>
          </div>

          {/* Preview */}
          <div className="glass-card p-6">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <FileText className="text-neon-blue" size={24} />
              Preview Kontrak
            </h2>
            <div className="bg-dark-lighter p-6 rounded-xl text-sm text-gray-300 space-y-3 max-h-[600px] overflow-y-auto">
              <h3 className="text-center font-bold text-white text-base">
                SURAT PERJANJIAN {formData.contractType.toUpperCase().replace('-', ' ')}
              </h3>
              <p>
                Pada hari ini, {new Date(formData.date).toLocaleDateString('id-ID', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
              <p>Yang bertanda tangan di bawah ini:</p>
              
              <div>
                <p className="font-bold text-neon-blue">PIHAK PERTAMA:</p>
                <p>Nama: {formData.party1Name || '___________'}</p>
                <p>Alamat: {formData.party1Address || '___________'}</p>
              </div>

              <div>
                <p className="font-bold text-neon-green">PIHAK KEDUA:</p>
                <p>Nama: {formData.party2Name || '___________'}</p>
                <p>Alamat: {formData.party2Address || '___________'}</p>
              </div>

              <div>
                <p className="font-bold text-white">Pasal 1 - Objek Perjanjian</p>
                <p>{formData.objectDescription || 'Deskripsi objek perjanjian akan muncul di sini...'}</p>
              </div>

              {formData.price && (
                <div>
                  <p className="font-bold text-white">Pasal 2 - Harga</p>
                  <p>Harga yang disepakati adalah Rp {parseInt(formData.price || '0').toLocaleString('id-ID')}</p>
                </div>
              )}

              <div className="flex justify-between mt-8 pt-6 border-t border-gray-700">
                <div className="text-center">
                  <p>Pihak Pertama,</p>
                  <div className="h-16"></div>
                  <p>({formData.party1Name || '_____'})</p>
                </div>
                <div className="text-center">
                  <p>Pihak Kedua,</p>
                  <div className="h-16"></div>
                  <p>({formData.party2Name || '_____'})</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
